/**
 * Navigation tree. Mirrors the reference sidebar and settings menu (labels and permission codes).
 * Only items with a `path` are rendered; the rest document what the reference has and what is still to build.
 */

export interface NavItem {
  label: string;
  /** Permission code, e.g. PC345. */
  code?: string;
  /** Client route. Absent = not built yet. */
  path?: string;
  /** Small label after the entry, as the reference's "NEW" on Customize Reports. */
  badge?: string;
  children?: NavItem[];
}

const n = (label: string, code?: number, pathOrChildren?: string | NavItem[]): NavItem => ({
  label,
  code: code ? `PC${code}` : undefined,
  ...(typeof pathOrChildren === 'string' ? { path: pathOrChildren } : {}),
  ...(Array.isArray(pathOrChildren) ? { children: pathOrChildren } : {}),
});

export const SIDEBAR_NAV: NavItem[] = [
  n('Customer Service', 400, [
    n('Customer Profile', 56, '/customers'),
    n('Billing and Invoice', 75, '/customers/find/billing'),
    n('Order History', 76, '/customers/find/orders'),
    n('Purchase History', 58, '/customers/find/purchases'),
    n('Tickets', 87, '/customers/find/tickets'),
    n('Customer Activity Log', 770, '/customers/find/activity'),
    n('Manage Saved Card', 900, '/customers/find/cards'),
    n('Resend Notification', 930, '/customers/find/notifications'),
    n('Recent Searches', 295, '/customers/recent'),
    n('Payment Search Tool', 339, '/customers/payment-search'),
    n('Dealer Wallet', 544, '/dealer-wallet'),
  ]),
  n('Ports', 393, [
    n('Port In', 799, [
      n('Pending Portin', 394, '/ports/in/pending'),
      n('Portin Queue(Manual)', 394, '/ports/in/manual'),
      n('Failed Portin', 395, '/ports/in/failed'),
      n('Cancel Portin', 395, '/ports/in/cancelled'),
      n('Completed Portin', 396, '/ports/in/completed'),
      n('Existing Subscriber Portin', 343, '/ports/in/existing-subscriber'),
      n('Pending Port-in Swaps', 343, '/ports/in/swaps'),
      n('Portin History', 778, '/ports/in/history'),
    ]),
    n('Port Out', 800, [
      n('T-Mobile Completed Portout', 815, '/ports/out/tmobile-completed'),
      n('Plintron(ITG) Pending Portout', 923, '/ports/out/itg-pending'),
      n('Plintron(ITG) Completed Portout', 924, '/ports/out/itg-completed'),
      n('Plum Pending Portout', 937, '/ports/out/plum-pending'),
      n('Plum Completed Portout', 936, '/ports/out/plum-completed'),
    ]),
  ]),
  n('Prepaid/postpaid orders', 109, [
    n('New enrollments', 1142, '/enrollments/new'),
    n('All enrollments', 1170, '/enrollments'),
    n('Bulk enrollment', 731, '/enrollments/bulk'),
    n('Carrier check service availability', 816, '/enrollments/coverage-check'),
    n('Completed enrollments', 1171, '/enrollments/completed'),
    n('Incomplete enrollments', 1172, '/enrollments/incomplete'),
  ]),
  // The owner's request of 2026-10-28: Lifeline orders beside the prepaid ones.
  n('Lifeline', 1165, [
    n('New application', 1164, '/lifeline/new'),
    n('Search account', 1165, '/lifeline/search'),
    n('Check status', 1165, '/lifeline/status'),
    n('All applications', 1165, '/lifeline'),
    n('Completed applications', 1166, '/lifeline/completed'),
    n('Incomplete applications', 1167, '/lifeline/incomplete'),
    n('Rejected applications', 1168, '/lifeline/rejected'),
    n('Carrier check service availability', 816, '/lifeline/coverage-check'),
  ]),
  n('Review', 426, [n('Fraud Orders Review Queue', 984, '/review/fraud-orders')]),
  n('Inventory', 382, [
    n('Manage Inventory', 345, '/inventory'),
    n('Dropship Orders', 7, '/inventory/dropship-orders'),
    n('Shipping Queue', 1066, '/inventory/shipping-queue'),
    n('Bulk Process', 347, [
      n('Bulk Clear SIM', 351, '/inventory/bulk/clear-sim'),
      n('Bulk Print Label', 884, '/inventory/bulk/print-label'),
      n('Bulk Reassign SIM', 352, '/inventory/bulk/reassign-sim'),
      n('Bulk Reassign Device', 353, '/inventory/bulk/reassign-device'),
      n('Bulk SIM Reassign by Agent', 552, '/inventory/bulk/reassign-sim-by-agent'),
      n('Bulk Device Reassign by Agent', 551, '/inventory/bulk/reassign-device-by-agent'),
      n('Bulk Clear Customer Device', 868, '/inventory/bulk/clear-customer-device'),
      n('Bulk Clear Device', 354, '/inventory/bulk/clear-device'),
      n('Bulk DeProvision SIM', 355, '/inventory/bulk/deprovision-sim'),
      n('Bulk Deactivate MDN', 356, '/inventory/bulk/deactivate-mdn'),
      n('Bulk Swap SIM', 358, '/inventory/bulk/swap-sim'),
      n('Bulk Swap MDN', 357, '/inventory/bulk/swap-mdn'),
      n('Bulk Swap MDN By customer ID', 804, '/inventory/bulk/swap-mdn-by-customer'),
      n('Bulk MDN Topup', 830, '/inventory/bulk/mdn-topup'),
      n('Bulk Customer Plan Change', 360, '/inventory/bulk/customer-plan-change'),
      n('Bulk Line Status', 361, '/inventory/bulk/line-status'),
      n('Bulk Hotline', 362, '/inventory/bulk/hotline'),
      n('Bulk Unhotline', 363, '/inventory/bulk/unhotline'),
      n('Bulk Device Set Status And Reassign', 364, '/inventory/bulk/device-status-reassign'),
      n('Bulk SIM Device Information', 1058, '/inventory/bulk/sim-device-information'),
      n('Bulk SIM Set Status And Reassign', 365, '/inventory/bulk/sim-status-reassign'),
      n('Bulk Customer Update', 856, '/inventory/bulk/customer-update'),
      n('Bulk NLAD Process', 857, '/inventory/bulk/nlad-process'),
    ]),
    n('Manage Model', 314, '/inventory/models'),
    n('Manage Equipment', 679, '/inventory/equipment'),
    n('Manage Equipment Type', 683, '/inventory/equipment-types'),
    n('Manage Equipment Status Name', 727, '/inventory/equipment-status-names'),
    n('Manage Change Reason', 732, '/inventory/change-reasons'),
    n('Inventory Report', 350, '/inventory/report'),
    n('Reporting', 19, '/inventory/reporting'),
  ]),
  n('Shipment', 383, [
    n('Shipment Dashboard', 223, '/shipment/dashboard'),
    n('Equipment & Accessories Request', 538, '/shipment/equipment-requests'),
  ]),
  n('Reports', 224, [
    n('All Reports', 428, '/reports'),
    n('Customize Reports', 429, '/reports/customize'),
    n('Saved report settings', 520, '/reports/saved'),
    n('Scheduled reports', 527, '/reports/scheduled'),
  ]),
  n('APIs', 431, [
    n('REST API docs', 806, '/rest-api-docs'),
    n('REST API Log', 808, '/logs/rest-api'),
  ]),
  n('Notifications', 677, [
    n('Add email notification', 668, '/notifications/email/new'),
    n('View email notifications', 669, '/notifications/email'),
    n('Add SMS notification', 675, '/notifications/sms/new'),
    n('View SMS notifications', 676, '/notifications/sms'),
    n('Add New IVR Notification', 832, '/notifications/ivr/new'),
    n('View IVR Notification', 833, '/notifications/ivr'),
  ]),
  n('Campaigns', 760, [
    n('Create Campaign', 761, '/campaigns/new'),
    n('View Campaign List', 762, '/campaigns'),
    n('Attach customers to a campaign', 763, '/campaigns/attach'),
  ]),
  n('Tickets', 722, '/tickets'),
  n('Coupon codes', 852, '/coupons'),
  n('USPS API Log', 1101, '/logs/usps-api'),
  n('Digital Card', 1141, '/digital-cards'),
  // 212Ez's own (owner, 2026-11-03): every integration in one place, where a company enters its
  // credentials. The same page as Settings > Connections.
  n('Integrations', 239, '/integrations'),
];

/** Settings (gear) menu. */
export const SETTINGS_NAV: NavItem[] = [
  n('Plan Config', 232, '/settings/plans'),
  n('User Settings', 480, '/settings/users'),
  n('System Config', 234, '/settings/system'),
  n('Shipper Settings', 236, '/settings/shipper'),
  n('Service Area', 237, '/settings/service-area'),
  n('Unlock User', 238, '/settings/unlock-user'),
  n('Payment Gateway', 239, '/settings/payment-gateway'),
  // 212Ez's own (owner's request, 2026-10-30): a new MVNO's set-up steps and go-live request,
  // and the company's provider accounts.
  n('Get started', 239, '/settings/get-started'),
  n('Connections', 239, '/settings/connections'),
  n('Tax Config', 1203, '/settings/tax-config'),
  n('Other Settings', 240, '/settings/other'),
];

/** Keeps items that are built and that the user may see; drops empty branches. */
export function filterNav(items: NavItem[], can: (code: string) => boolean): NavItem[] {
  const out: NavItem[] = [];
  for (const item of items) {
    if (item.code && !can(item.code)) continue;
    if (item.children) {
      const children = filterNav(item.children, can);
      if (children.length) out.push({ ...item, children });
    } else if (item.path) {
      out.push(item);
    }
  }
  return out;
}

/** First route the user can open; used as the landing page. */
export function firstNavPath(can: (code: string) => boolean): string | undefined {
  const walk = (items: NavItem[]): string | undefined => {
    for (const item of items) {
      if (item.path) return item.path;
      if (item.children) {
        const found = walk(item.children);
        if (found) return found;
      }
    }
    return undefined;
  };
  return walk(filterNav(SIDEBAR_NAV, can)) ?? walk(filterNav(SETTINGS_NAV, can));
}
